import { type AgentRun } from '@lucidtech/las-sdk-browser';
import { AccessorKeyColumnDef } from '@tanstack/react-table';
import { memo, useEffect, useMemo, useRef } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { Button, DateTime, Heading, Loading, Text } from '@/components/core';
import { RunsIcon } from '@/components/icons';
import { AgentRunDocumentCard, AgentRunStatus, InviteMembersModal, Profile } from '@/components/ui';
import { ManagedRole } from '@/constants';
import { SampleDocumentTooltip } from '@/features/agents';
import { useGetAgentFromPath, useListReviewers, useMeasuredSize } from '@/hooks';
import { toModelId, useGetModel } from '@/hooks/api';
import { InboxProvider } from '@/store';
import { findModelId, isNumber } from '@/utils';

import { AgentRunEventsCell } from './AgentRunEventsCell';
import { AgentRunsTable } from './AgentRunsTable';
import { ConfigureModelCTA } from './ConfigureModelCTA.tsx';
import { InboxSelectedToolbar } from './InboxSelectedToolbar';
import { RunOverflowMenu } from './RunOverflowMenu.tsx';
import { RunsActionBar } from './RunsActionBar.tsx';
import { SetupAutomaticExportBanner } from './SetupAutomaticExportBanner.tsx';

const INBOX_WIDTH_KEY = 'cradl:inboxWidth';

export type InboxProps = object;

// eslint-disable-next-line no-empty-pattern
const IInbox = ({}: InboxProps) => {
  const widthRef = useRef<HTMLDivElement | null>(null);
  const { width: tableWidth } = useMeasuredSize(widthRef, {
    width: () => {
      const storedInboxWidth = localStorage.getItem(INBOX_WIDTH_KEY);
      const inboxWidth = storedInboxWidth ? Number(storedInboxWidth) : undefined;
      return isNumber(inboxWidth) ? inboxWidth : undefined;
    },
  });

  useEffect(() => {
    if (isNumber(tableWidth)) {
      localStorage.setItem(INBOX_WIDTH_KEY, tableWidth.toString());
    }
  }, [tableWidth]);

  const columns = useMemo<AccessorKeyColumnDef<AgentRun>[]>(() => {
    return [
      {
        accessorKey: 'file',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            File
          </Heading>
        ),
        cell: ({ row }) => <AgentRunDocumentCard agentId={row.original.agentId} runId={row.original.runId} />,
        size: isNumber(tableWidth) ? (tableWidth - 450) / 3 : undefined,
        enableSorting: false,
      },
      {
        accessorKey: 'createdTime',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            Started
          </Heading>
        ),
        cell: ({ row }) => <DateTime date={row.original.createdTime} />,
        size: 150,
      },
      {
        accessorKey: 'updatedTime',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            Ended
          </Heading>
        ),
        cell: ({ row }) => (row.original.status === 'completed' ? <DateTime date={row.original.updatedTime} /> : null),
        size: 150,
      },
      {
        accessorKey: 'events',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            Events
          </Heading>
        ),
        cell: ({ row }) => <AgentRunEventsCell agentId={row.original.agentId} runId={row.original.runId} />,
        size: isNumber(tableWidth) ? (tableWidth - 450) / 2 : undefined,
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            Status
          </Heading>
        ),
        cell: ({ row }) => <AgentRunStatus agentId={row.original.agentId} runId={row.original.runId} />,
        enableSorting: false,
        size: 150,
      },
      {
        accessorKey: 'other',
        header: () => null,
        cell: ({ row }) => <RunOverflowMenu agentId={row.original.agentId} runId={row.original.runId} />,
        enableSorting: false,
        size: 25,
      },
    ];
  }, [tableWidth]);

  const { data: agent } = useGetAgentFromPath();
  const { data: model, isFetched: modelIsFetched } = useGetModel(toModelId(findModelId(agent)));
  const { users } = useListReviewers({ agentId: agent?.agentId });

  const isModelConfigured = useMemo(() => {
    return Object.entries(model?.fieldConfig ?? {}).length;
  }, [model]);

  if (!agent) {
    return <Loading label="Loading runs ..." />;
  }

  return (
    <div className="flex min-h-0 w-full flex-col">
      <div className="py-md gap-sm flex h-12 w-full items-center border-b-1 border-gray-200 bg-white px-12">
        <RunsIcon />
        <Heading className="text-gray-600" size="md">
          Runs
        </Heading>
        <div className="grow" />
        <div className="flex items-center">
          {users?.map((user) => (
            <Profile
              className="-mx-0.5 bg-white"
              profileId={user.profileId}
              showEmail={false}
              showName={false}
              props={{
                img: {
                  className:
                    'size-6 bg-white outline-1 outline-[rgba(0,0,0,0.08)] -outline-offset-2 border-1 border-white shadow-md',
                },
              }}
            />
          ))}
        </div>
        <DialogTrigger>
          <Button variant="plain">
            <Text>Invite</Text>
          </Button>
          <InviteMembersModal defaultRole={ManagedRole.Reviewer} />
        </DialogTrigger>
      </div>
      <div ref={widthRef} className="gap-sm flex min-h-0 w-full grow flex-col bg-gray-50 px-12 pt-10">
        <InboxProvider agentId={agent?.agentId}>
          <RunsActionBar agentId={agent.agentId} />
          {isModelConfigured ? (
            <>
              <SetupAutomaticExportBanner agentId={agent.agentId} />
              <SampleDocumentTooltip className="translate-x-9" offset={96} agentId={agent.agentId} />
              <AgentRunsTable agentId={agent.agentId} columns={columns} />
              <InboxSelectedToolbar agentId={agent.agentId} />
            </>
          ) : (
            <>{modelIsFetched && <ConfigureModelCTA agentId={agent?.agentId} />}</>
          )}
        </InboxProvider>
      </div>
    </div>
  );
};

export const Inbox = memo(IInbox) as typeof IInbox;
